/**
 * Per-user, persisted snapshot of the connector list (stale-while-revalidate).
 *
 * The connectors screen renders the last snapshot immediately and refetches in the
 * background. Every access is try/catch-wrapped (private windows, blocked storage and
 * quota errors must never break the page) and keyed by user id so one account can never
 * see another account's connectors.
 *
 * Plain `localStorage` is used on purpose: it behaves the same in the web app and in the
 * Electron renderer.
 *
 * @example
 * writeConnectorSnapshot("u1", { workflows: [{ workflow_id: "wf-1" }] });
 * readConnectorSnapshot("u1");
 * // { savedAt: 1730000000000, workflows: [{ workflow_id: "wf-1" }], crmStatusById: null }
 * readConnectorSnapshot("u2");
 * // null
 */

export const CONNECTOR_SNAPSHOT_KEY_PREFIX = "starmesh.connectorSnapshot.v1:";

/** Older than this is treated as absent, so a long-abandoned tab does not flash very old rows. */
export const CONNECTOR_SNAPSHOT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const keyForUser = (userId) => {
  const id = String(userId ?? "").trim();
  return id ? `${CONNECTOR_SNAPSHOT_KEY_PREFIX}${id}` : "";
};

/**
 * Read the snapshot for one user, or null when absent, expired, malformed or unreadable.
 *
 * @example
 * readConnectorSnapshot("u1");
 * // null
 */
export const readConnectorSnapshot = (userId, { now = Date.now() } = {}) => {
  const key = keyForUser(userId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const savedAt = Number(parsed.savedAt) || 0;
    if (!savedAt || now - savedAt > CONNECTOR_SNAPSHOT_MAX_AGE_MS) return null;
    return {
      savedAt,
      workflows: Array.isArray(parsed.workflows) ? parsed.workflows : null,
      crmStatusById:
        parsed.crmStatusById && typeof parsed.crmStatusById === "object"
          ? parsed.crmStatusById
          : null,
      performerStatus:
        parsed.performerStatus && typeof parsed.performerStatus === "object"
          ? parsed.performerStatus
          : null,
      performerConfig:
        parsed.performerConfig && typeof parsed.performerConfig === "object"
          ? parsed.performerConfig
          : null,
    };
  } catch (_) {
    return null;
  }
};

/**
 * Merge `patch` (`workflows`, `crmStatusById`, `performerStatus` and/or `performerConfig`) into the user's snapshot.
 *
 * @example
 * writeConnectorSnapshot("u1", { workflows: [] });
 * // true
 */
export const writeConnectorSnapshot = (userId, patch = {}, { now = Date.now() } = {}) => {
  const key = keyForUser(userId);
  if (!key) return false;
  try {
    const previous = readConnectorSnapshot(userId, { now }) || {};
    const next = {
      savedAt: now,
      workflows: Array.isArray(patch.workflows)
        ? patch.workflows
        : previous.workflows || null,
      crmStatusById:
        patch.crmStatusById && typeof patch.crmStatusById === "object"
          ? patch.crmStatusById
          : previous.crmStatusById || null,
      performerStatus:
        patch.performerStatus && typeof patch.performerStatus === "object"
          ? patch.performerStatus
          : previous.performerStatus || null,
      performerConfig:
        patch.performerConfig && typeof patch.performerConfig === "object"
          ? patch.performerConfig
          : previous.performerConfig || null,
    };
    window.localStorage.setItem(key, JSON.stringify(next));
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Drop one user's snapshot (or every user's when `userId` is omitted, e.g. on sign-out).
 *
 * @example
 * clearConnectorSnapshot();
 * // removes every starmesh.connectorSnapshot.v1:* key
 */
export const clearConnectorSnapshot = (userId) => {
  try {
    if (userId !== undefined && userId !== null) {
      const key = keyForUser(userId);
      if (key) window.localStorage.removeItem(key);
      return;
    }
    const storage = window.localStorage;
    const doomed = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(CONNECTOR_SNAPSHOT_KEY_PREFIX)) doomed.push(key);
    }
    doomed.forEach((key) => storage.removeItem(key));
  } catch (_) {
    // Storage unavailable: nothing to clear.
  }
};
